import classnames from 'classnames';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BorderedSwitch } from 'components/shared/BorderedSwitch';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FieldErrorWrapper } from 'components/shared/FormGroup/ErrorMessages';
import { Switch } from 'components/shared/Switch/Switch';
import type { IntlKey } from 'locales';
import type { ToggleFieldData, ToggleFieldMetadata } from 'models/forms/fields';
import { FC, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { camelCaseToWords } from 'utils/string';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

const ToggleField: FC<ProductNodeBuilderProps> = props => {
  const {
    node: { id },
    workspaceType,
    tabIndex,
  } = props;
  const { formatMessage } = useIntl();
  const [value, setValue] = useData<Extract<ToggleFieldData, boolean>>(id);
  const { hasTooltip } = useFieldMetadata<ToggleFieldMetadata>(id)!;
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const translationKey = useFieldTranslationKey(id);
  const orientation = useContext(FormOrientationContext);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const label = useMemo(() => {
    switch (workspaceType) {
      case 'dynamic':
        return camelCaseToWords(id);

      default:
        return formatMessage({ id: `form.${translationKey}` as IntlKey, defaultMessage: camelCaseToWords(id) });
    }
  }, [formatMessage, translationKey, id, workspaceType]);

  const tooltipTranslationKey = hasTooltip ? (`form.${translationKey}.tooltip` as any) : undefined;

  if (orientation === 'vertical') {
    return (
      <BorderedSwitch
        id={id}
        isDisabled={isReadOnly}
        value={value ?? false}
        onValueChanged={setValue}
        label={label}
        tooltipKey={tooltipTranslationKey}
        className={classnames('mb-2', patchedFieldClassName)}
        tabIndex={tabIndex}
      />
    );
  } else {
    return (
      <BlockOrientationFormGroup
        inputId={id}
        label={`form.${translationKey}` as IntlKey}
        workspaceType={workspaceType}
        className={patchedFieldClassName}
      >
        <Switch id={id} isDisabled={isReadOnly} value={value ?? false} onToggleSwitch={setValue} tabIndex={tabIndex} />
      </BlockOrientationFormGroup>
    );
  }
};

ToggleField.displayName = 'ToggleField';

export const ToggleFieldWrappedWithErrors: FC<ProductNodeBuilderProps> = props => (
  <FieldErrorWrapper inputId={props.node.id}>
    <ToggleField {...props} />
  </FieldErrorWrapper>
);
ToggleFieldWrappedWithErrors.displayName = 'ToggleFieldWrappedWithErrors';
